import { CountHeader, CountHeaderSize, Text, TextVariants, ToggleActionButton, ToggleActionMode } from "@next-platform/client-common-spicy-ui";
import { useTranslation, useUserSettings } from "@next-platform/client-common-core";
import { CHARACTER_VISIBILITY } from "@next-platform/client-common-core/utils/sharedTypes";
import { useCallback, useMemo } from "react";

import { cn } from "@/utils";
import { hasVisibilityConflict } from "@/utils/groupChatVisibility";

import MemberChip from "./MemberChip";
import { GroupMembersChatBot, MembersGroupChatDisplayMode } from "./types";

export interface IMembersGroupChatProps {
  title?: string;
  helperText?: string;
  members?: GroupMembersChatBot[];
  maxMembers?: number;
  mode?: MembersGroupChatDisplayMode;
  onSave?: (members: GroupMembersChatBot[]) => void;
  onAddRemove?: () => void;
  onRemoveMember?: (memberId: string) => void;
  required?: boolean;
  disabled?: boolean;
  hideHeader?: boolean;
  visibility?: string;
  classNames?: {
    container?: string;
    title?: string;
    count?: string;
    required?: string;
  };
  isEdit?: boolean;
}

const DEFAULT_CLASS_NAMES: IMembersGroupChatProps["classNames"] = {};

const MembersGroupChat = ({
  title = "Group members",
  helperText,
  members = [],
  maxMembers = 10,
  mode = MembersGroupChatDisplayMode.NORMAL,
  onSave,
  onAddRemove,
  onRemoveMember,
  disabled = false,
  hideHeader = false,
  visibility = CHARACTER_VISIBILITY.PUBLIC,
  classNames = DEFAULT_CLASS_NAMES,
  isEdit = false,
}: IMembersGroupChatProps) => {
  const { t } = useTranslation();
  const { blurNSFW } = useUserSettings();

  const handleAddRemove = useCallback(() => {
    if (onAddRemove) {
      onAddRemove();
    }
  }, [onAddRemove]);

  const handleSave = useCallback(() => {
    if (mode === MembersGroupChatDisplayMode.EDITABLE && onSave) {
      onSave(members);
    } else {
      // if the condition is not met the component the button is disabled"
    }
  }, [mode, onSave, members]);

  const handleRemoveMember = useCallback(
    (memberId: string) => {
      onRemoveMember?.(memberId);
    },
    [onRemoveMember],
  );

  const validatedMembers = useMemo(() => {
    return members.map((member: GroupMembersChatBot) => ({
      ...member,
      hasError: hasVisibilityConflict(visibility, member.visibility),
    }));
  }, [members, visibility]);

  const secondaryLabel = useMemo(() => {
    if (members?.length > 0) {
      return t("createGroup:form.field.members.secondaryLabel.addRemove");
    }
    if (isEdit) {
      return t("createGroup:form.field.members.secondaryLabel.edit");
    }
    return t("createGroup:form.field.members.secondaryLabel.add");
  }, [members?.length, isEdit, t]);

  const isEditable = mode === MembersGroupChatDisplayMode.EDITABLE;

  return (
    <div className="bg-transparent">
      {!hideHeader && (
        <div>
          <CountHeader
            title={title}
            size={isEditable ? CountHeaderSize.BIG : CountHeaderSize.SMALL}
            currentCount={members.length}
            maxCount={maxMembers}
            classNames={classNames}
          />
          {!!helperText && (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="mt-1 text-gray-11">
              {helperText}
            </Text>
          )}
        </div>
      )}

      <div className="mt-4">
        <ToggleActionButton
          mode={isEditable ? ToggleActionMode.PRIMARY : ToggleActionMode.SECONDARY}
          onPrimaryClick={handleSave}
          onSecondaryClick={handleAddRemove}
          disabled={disabled}
          secondaryLabel={secondaryLabel}
          primaryLabel={t("createGroup:form.button.saveMembers")}
        />
      </div>

      <div className={cn("flex flex-wrap gap-2 mt-4")}>
        {validatedMembers.map((member: GroupMembersChatBot) => (
          <MemberChip key={member.id} member={member} editable={isEditable} blurNSFW={blurNSFW} onRemove={handleRemoveMember} />
        ))}
      </div>
    </div>
  );
};

MembersGroupChat.displayName = "MembersGroupChat";

export default MembersGroupChat;
